/**
 * Goods Receipt Detail Page
 * Epic 21: Bevételezés részletei + műveletek
 *
 * Features:
 *   - Header: receipt number, supplier, date, status badge
 *   - Items table: product code, product name, expected qty, received qty, discrepancy badge
 *   - "Befejezés" button (complete receipt → inventory update)
 *   - Discrepancy section: list discrepancies, resolve button
 *   - Link to purchase advice if available
 */

import type { DiscrepancyType, ReceiptStatus } from '@/api/goods-receipt';
import { distributeShipping, type ShippingAllocationItem } from '@/api/goods-receipt';
import { splitReceiptAvailability, type SplitAvailabilityItem } from '@/api/inventory';
import { Button, Card, CardContent } from '@/components/ui';
import { Input } from '@/components/ui/input';
import {
  useCompleteReceipt,
  useDiscrepancies,
  useReceipt,
  useResolveDiscrepancy,
} from '@/hooks/useGoodsReceipt';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

const STATUS_COLORS: Record<ReceiptStatus, string> = {
  DRAFT: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  DISCREPANCY: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  COMPLETED: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
};

const DISCREPANCY_TYPE_COLORS: Record<DiscrepancyType, string> = {
  SHORTAGE: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
  SURPLUS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  DAMAGED: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  WRONG_ITEM: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString('hu-HU');
}

export function GoodsReceiptDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { receipt, isLoading, error } = useReceipt(id);
  const { discrepancies } = useDiscrepancies(id);
  const completeMutation = useCompleteReceipt();
  const resolveMutation = useResolveDiscrepancy();

  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [splitItems, setSplitItems] = useState<SplitAvailabilityItem[] | null>(null);
  const [splitLoading, setSplitLoading] = useState(false);

  // SALES-FG-4-3: Shipping cost distribution state
  const [shippingCost, setShippingCost] = useState('');
  const [shippingItems, setShippingItems] = useState<ShippingAllocationItem[] | null>(null);
  const [shippingLoading, setShippingLoading] = useState(false);

  const handleDistributeShipping = async () => {
    if (!id) return;
    const cost = parseInt(shippingCost, 10);
    if (!cost || cost <= 0) return;
    setShippingLoading(true);
    try {
      const result = await distributeShipping(id, cost);
      setShippingItems(result.data.items);
    } catch {
      // silently fail — user can retry
    } finally {
      setShippingLoading(false);
    }
  };

  const handleSplitAvailability = async () => {
    if (!id) return;
    setSplitLoading(true);
    try {
      const result = await splitReceiptAvailability(id);
      setSplitItems(result.data.items);
    } catch {
      // silently fail — user can retry
    } finally {
      setSplitLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!id) return;
    const confirmed = window.confirm(t('goodsReceipt.detail.completeConfirm'));
    if (!confirmed) return;
    await completeMutation.mutateAsync(id);
  };

  const handleResolve = async (discrepancyId: string) => {
    if (!id || !resolutionNote.trim()) return;
    await resolveMutation.mutateAsync({
      receiptId: id,
      discrepancyId,
      input: { resolutionNote },
    });
    setResolvingId(null);
    setResolutionNote('');
  };

  if (isLoading) {
    return <div className="p-6 text-center text-muted-foreground">{t('common.loading')}</div>;
  }

  if (error || !receipt) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500">{error ?? t('goodsReceipt.detail.notFound')}</p>
        <Button variant="outline" onClick={() => navigate('/goods-receipt')} className="mt-4">
          {t('goodsReceipt.detail.backToList')}
        </Button>
      </div>
    );
  }

  const unresolvedCount = discrepancies.filter(d => !d.resolved).length;
  const canComplete = receipt.status !== 'COMPLETED' && unresolvedCount === 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            onClick={() => navigate('/goods-receipt')}
            className="mb-2 text-sm text-muted-foreground hover:text-foreground"
          >
            &larr; {t('goodsReceipt.detail.backToList')}
          </button>
          <h1 className="text-2xl font-bold text-foreground">{receipt.receiptNumber}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {receipt.status !== 'COMPLETED' && (
            <Button variant="outline" onClick={handleSplitAvailability} disabled={splitLoading}>
              {splitLoading ? '...' : t('inventory.splitAvailability.split')}
            </Button>
          )}
          {canComplete && (
            <Button
              onClick={handleComplete}
              disabled={completeMutation.isPending}
              className="bg-green-600 text-white hover:bg-green-700"
            >
              {completeMutation.isPending
                ? t('goodsReceipt.detail.completing')
                : t('goodsReceipt.detail.complete')}
            </Button>
          )}
          {unresolvedCount > 0 && receipt.status !== 'COMPLETED' && (
            <span className="inline-flex items-center rounded-lg bg-orange-50 px-3 py-2 text-sm text-orange-800 dark:bg-orange-900/20 dark:text-orange-300">
              {t('goodsReceipt.detail.unresolvedCount', { count: unresolvedCount })}
            </span>
          )}
        </div>
      </div>

      {/* Details Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('table.status')}</p>
              <span
                className={`mt-1 inline-block rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[receipt.status]}`}
              >
                {t(
                  `goodsReceipt.statusLabels.${receipt.status === 'IN_PROGRESS' ? 'inProgress' : receipt.status.toLowerCase()}`
                )}
              </span>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('goodsReceipt.detail.supplier')}</p>
              <p className="mt-1 font-medium text-foreground">{receipt.supplierName}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('goodsReceipt.detail.createdAt')}</p>
              <p className="mt-1 text-sm text-foreground">{formatDate(receipt.createdAt)}</p>
            </div>
            {receipt.completedAt && (
              <div>
                <p className="text-sm text-muted-foreground">
                  {t('goodsReceipt.detail.completedAt')}
                </p>
                <p className="mt-1 text-sm text-foreground">{formatDate(receipt.completedAt)}</p>
              </div>
            )}
          </div>

          {receipt.purchaseAdviceId && (
            <div className="mt-4 border-t pt-4 dark:border-gray-700">
              <p className="text-sm text-muted-foreground">
                {t('goodsReceipt.detail.linkedAvizo')}
              </p>
              <p className="mt-1 text-sm font-medium text-blue-600 dark:text-blue-400">
                {receipt.purchaseAdviceId}
              </p>
            </div>
          )}

          {receipt.notes && (
            <div className="mt-4 border-t pt-4 dark:border-gray-700">
              <p className="text-sm text-muted-foreground">{t('goodsReceipt.detail.notes')}</p>
              <p className="mt-1 text-sm text-foreground">{receipt.notes}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Items Table */}
      <Card>
        <div className="border-b bg-gray-50 px-4 py-3 dark:bg-gray-800 dark:border-gray-700">
          <h2 className="font-medium text-foreground">
            {t('goodsReceipt.detail.items')} ({receipt.items?.length ?? 0})
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-sm text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">{t('goodsReceipt.detail.productCode')}</th>
                <th className="px-4 py-3 font-medium">{t('goodsReceipt.detail.productName')}</th>
                <th className="px-4 py-3 font-medium text-right">
                  {t('goodsReceipt.detail.expectedQty')}
                </th>
                <th className="px-4 py-3 font-medium text-right">
                  {t('goodsReceipt.detail.receivedQty')}
                </th>
                <th className="px-4 py-3 font-medium text-center">
                  {t('goodsReceipt.detail.difference')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {(receipt.items ?? []).map(item => {
                const diff = item.receivedQuantity - item.expectedQuantity;
                const hasDiff = diff !== 0;
                return (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3 text-sm font-mono text-foreground">
                      {item.productCode}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {item.productName}
                      {item.purchaseCategory && (
                        <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-muted-foreground dark:bg-gray-700">
                          {item.purchaseCategory}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-foreground">
                      {item.expectedQuantity}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-foreground">
                      {item.receivedQuantity}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {hasDiff ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            diff < 0
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                          }`}
                        >
                          {diff > 0 ? '+' : ''}
                          {diff}
                        </span>
                      ) : (
                        <span className="text-sm text-green-600 dark:text-green-400">
                          {t('goodsReceipt.detail.ok')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* SALES-FG-4-3: Shipping Cost Distribution */}
      {receipt.status !== 'COMPLETED' && (
        <Card>
          <div className="border-b bg-gray-50 px-4 py-3 dark:bg-gray-800 dark:border-gray-700">
            <h2 className="font-medium text-foreground">{t('goodsReceipt.shipping.title')}</h2>
          </div>
          <CardContent className="pt-4">
            <div className="flex items-end gap-3">
              <div className="flex-1 max-w-xs">
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  {t('goodsReceipt.shipping.costLabel')}
                </label>
                <Input
                  type="number"
                  value={shippingCost}
                  onChange={e => setShippingCost(e.target.value)}
                  placeholder={t('goodsReceipt.shipping.costPlaceholder')}
                  min="1"
                />
              </div>
              <Button
                onClick={handleDistributeShipping}
                disabled={shippingLoading || !shippingCost || parseInt(shippingCost, 10) <= 0}
              >
                {shippingLoading ? '...' : t('goodsReceipt.shipping.distribute')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* SALES-FG-4-3: Shipping Allocation Results */}
      {shippingItems && shippingItems.length > 0 && (
        <Card>
          <div className="border-b bg-green-50 px-4 py-3 dark:bg-green-900/20 dark:border-gray-700">
            <h2 className="font-medium text-green-800 dark:text-green-300">
              {t('goodsReceipt.shipping.allocationTitle')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-sm text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('goodsReceipt.detail.productName')}</th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t('goodsReceipt.shipping.originalPrice')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t('goodsReceipt.shipping.proportion')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t('goodsReceipt.shipping.allocation')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t('goodsReceipt.shipping.adjustedPrice')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {shippingItems.map(item => (
                  <tr key={item.receiptItemId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3 text-sm text-foreground">{item.productName}</td>
                    <td className="px-4 py-3 text-right text-sm text-foreground">
                      {item.originalUnitPrice.toLocaleString('hu-HU')} Ft
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-muted-foreground">
                      {item.proportion}%
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-green-600 dark:text-green-400">
                      +{item.shippingAllocation.toLocaleString('hu-HU')} Ft
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-foreground">
                      {item.adjustedUnitPrice.toLocaleString('hu-HU')} Ft
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* INV-FG-7-2: Split Availability Visualization */}
      {splitItems && splitItems.length > 0 && (
        <Card>
          <div className="border-b bg-blue-50 px-4 py-3 dark:bg-blue-900/20 dark:border-gray-700">
            <h2 className="font-medium text-blue-800 dark:text-blue-300">
              {t('inventory.splitAvailability.title')}
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 text-left text-sm text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">{t('goodsReceipt.detail.productName')}</th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t('goodsReceipt.detail.expectedQty')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t('inventory.splitAvailability.available')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right">
                    {t('inventory.splitAvailability.backordered')}
                  </th>
                  <th className="px-4 py-3 font-medium text-center">{t('table.status')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {splitItems.map(si => (
                  <tr key={si.receiptItemId} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-4 py-3 text-sm text-foreground">{si.productName}</td>
                    <td className="px-4 py-3 text-right text-sm text-foreground">
                      {si.expectedQuantity}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-green-600 dark:text-green-400">
                      {si.availableNow}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-orange-600 dark:text-orange-400">
                      {si.backordered > 0 ? si.backordered : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                          si.status === 'AVAILABLE'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                            : si.status === 'BACKORDERED'
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'
                              : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300'
                        }`}
                      >
                        {t(
                          `inventory.splitAvailability.${si.status === 'AVAILABLE' ? 'fullyAvailable' : si.status === 'BACKORDERED' ? 'fullyBackordered' : 'partial'}`
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Discrepancies Section */}
      {discrepancies.length > 0 && (
        <Card>
          <div className="border-b bg-orange-50 px-4 py-3 dark:bg-orange-900/20 dark:border-gray-700">
            <h2 className="font-medium text-orange-800 dark:text-orange-300">
              {t('goodsReceipt.detail.discrepancies')} ({discrepancies.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {discrepancies.map(disc => (
              <div key={disc.id} className="px-4 py-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${DISCREPANCY_TYPE_COLORS[disc.type]}`}
                    >
                      {t(`goodsReceipt.discrepancyTypes.${disc.type.toLowerCase()}`)}
                    </span>
                    <span className="text-sm text-foreground">
                      {t('goodsReceipt.detail.expected')}: {disc.expectedQuantity} |{' '}
                      {t('goodsReceipt.detail.actual')}: {disc.actualQuantity}
                    </span>
                    {disc.reason && (
                      <span className="text-sm text-muted-foreground">({disc.reason})</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {disc.resolved ? (
                      <span className="text-sm text-green-600 dark:text-green-400">
                        {t('goodsReceipt.detail.resolved')}
                        {disc.resolutionNote && `: ${disc.resolutionNote}`}
                      </span>
                    ) : receipt.status !== 'COMPLETED' ? (
                      resolvingId === disc.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            value={resolutionNote}
                            onChange={e => setResolutionNote(e.target.value)}
                            placeholder={t('goodsReceipt.detail.resolutionNotePlaceholder')}
                            className="rounded border px-2 py-1 text-sm dark:border-gray-600 dark:bg-gray-800 text-foreground"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleResolve(disc.id)}
                            disabled={!resolutionNote.trim() || resolveMutation.isPending}
                            className="bg-green-600 text-white hover:bg-green-700"
                          >
                            {resolveMutation.isPending
                              ? t('common.saving')
                              : t('goodsReceipt.detail.resolveButton')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setResolvingId(null);
                              setResolutionNote('');
                            }}
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setResolvingId(disc.id)}>
                          {t('goodsReceipt.detail.resolveButton')}
                        </Button>
                      )
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Complete mutation error */}
      {completeMutation.isError && (
        <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-300">
          {completeMutation.error?.message ?? t('common.error')}
        </div>
      )}
    </div>
  );
}
